import { apiClient } from './api-client';

export type DayOfWeek =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export interface TimeSlot {
  start: string; // HH:mm
  end: string; // HH:mm
}

export interface DaySchedule {
  enabled: boolean;
  slots: TimeSlot[];
}

export interface Holiday {
  name: string;
  date: string; // YYYY-MM-DD
  recurring?: boolean;
}

export type WeeklySchedule = Record<DayOfWeek, DaySchedule>;

export interface CreateBusinessHoursDto {
  name: string;
  description?: string;
  timezone: string;
  isDefault?: boolean;
  schedule?: WeeklySchedule;
  holidays?: Holiday[];
}

export interface UpdateBusinessHoursDto extends Partial<CreateBusinessHoursDto> {
  isActive?: boolean;
}

export interface BusinessHoursSchedule {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  timezone: string;
  isDefault: boolean;
  isActive: boolean;
  schedule: WeeklySchedule;
  holidays: Holiday[];
  createdAt: string;
  updatedAt: string;
}

/** A single thing using a schedule, named so the delete warning can list it. */
export interface BusinessHoursReference {
  id: string;
  name: string;
}

/**
 * What currently points at a schedule. Advisory: a schedule in use can still be
 * deleted — those consumers just fall back to calendar time.
 */
export interface BusinessHoursUsage {
  inUse: boolean;
  slaPolicies: BusinessHoursReference[];
  pipelines: BusinessHoursReference[];
}

export const businessHoursService = {
  async getSchedules(): Promise<BusinessHoursSchedule[]> {
    const response = await apiClient.get('/business-hours');
    return response.data;
  },

  async getDefaultSchedule(): Promise<BusinessHoursSchedule | null> {
    try {
      const response = await apiClient.get('/business-hours/default');
      return response.data;
    } catch {
      return null;
    }
  },

  async getSchedule(id: string): Promise<BusinessHoursSchedule> {
    const response = await apiClient.get(`/business-hours/${id}`);
    return response.data;
  },

  async createSchedule(data: CreateBusinessHoursDto): Promise<BusinessHoursSchedule> {
    const response = await apiClient.post('/business-hours', data);
    return response.data;
  },

  async updateSchedule(
    id: string,
    data: UpdateBusinessHoursDto,
  ): Promise<BusinessHoursSchedule> {
    const response = await apiClient.put(`/business-hours/${id}`, data);
    return response.data;
  },

  async getUsage(id: string): Promise<BusinessHoursUsage> {
    const response = await apiClient.get(`/business-hours/${id}/usage`);
    return response.data;
  },

  async deleteSchedule(id: string): Promise<void> {
    await apiClient.delete(`/business-hours/${id}`);
  },
};

export default businessHoursService;
